import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { BackgroundCheckMissingData } from "@src/appV2/OpenShifts/ShiftAction/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { Route, Switch, useHistory, useLocation, useRouteMatch } from "react-router-dom";

import {
  BACKGROUND_CHECK_COMPLETED_PATH,
  BACKGROUND_CHECK_CONSENT_PATH,
  BACKGROUND_CHECK_DETAILS_PATH,
  BACKGROUND_CHECK_INTRO_PATH,
} from "../Documents/paths";
import { useCreateBackgroundCheckCandidate } from "./apis/useCreateBackgroundCheckCandidate";
import { BackgroundCheckCompletedPage } from "./BackgroundCheckCompletedPage";
import { BackgroundCheckConsentPage } from "./BackgroundCheckConsentPage";
import { BackgroundCheckDetailsPage } from "./BackgroundCheckDetailsPage";
import { BackgroundCheckIntroPage } from "./BackgroundCheckIntroPage";
import {
  BACKGROUND_CHECK_BUTTON_ACTION,
  BACKGROUND_CHECK_PAGE_SOURCE,
  REQUIRED_DATA_QUERY_PARAM_KEY,
  RETURN_TO_QUERY_PARAM_KEY,
  SHIFT_ID_QUERY_PARAM_KEY,
} from "./constants";

export function BackgroundChecksRouter() {
  const match = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const worker = useDefinedWorker();
  const { showErrorToast } = useToast();
  const { mutateAsync: createBackgroundCheckCandidate, isLoading } =
    useCreateBackgroundCheckCandidate();

  const queryParams = useSearchParams<{
    [REQUIRED_DATA_QUERY_PARAM_KEY]: string[] | undefined;
    [SHIFT_ID_QUERY_PARAM_KEY]: string | undefined;
    [RETURN_TO_QUERY_PARAM_KEY]: string | undefined;
  }>();

  const shiftId = queryParams[SHIFT_ID_QUERY_PARAM_KEY];
  const requiredData = queryParams[REQUIRED_DATA_QUERY_PARAM_KEY] ?? [];
  const returnTo = queryParams[RETURN_TO_QUERY_PARAM_KEY];

  const computedSteps: string[] = [BACKGROUND_CHECK_INTRO_PATH];

  if (
    requiredData.includes(BackgroundCheckMissingData.DATE_OF_BIRTH) ||
    requiredData.includes(BackgroundCheckMissingData.SOCIAL_SECURITY_NUMBER)
  ) {
    computedSteps.push(BACKGROUND_CHECK_DETAILS_PATH);
  }

  if (requiredData.includes(BackgroundCheckMissingData.CONSENT)) {
    computedSteps.push(BACKGROUND_CHECK_CONSENT_PATH);
  }

  const goToNextStep = (currentPath: string) => {
    const currentIndex = computedSteps.indexOf(currentPath);
    const nextStep = computedSteps[currentIndex + 1] || BACKGROUND_CHECK_COMPLETED_PATH;
    // We're replacing instead of pushing because if they try to go back
    // after completing these pages, we don't want them to land on background check pages again.
    history.replace(`${nextStep}${location.search}`);
  };

  const goToPreviousStep = (currentPath: string) => {
    const currentIndex = computedSteps.indexOf(currentPath);
    const previousStep = computedSteps[currentIndex - 1] || BACKGROUND_CHECK_INTRO_PATH;
    // We're replacing instead of pushing because if they try to go back
    // after completing these pages, we don't want them to land on background check pages again.
    history.replace(`${previousStep}${location.search}`);
  };

  return (
    <Switch>
      <Route exact path={`${match.url}/${BACKGROUND_CHECK_INTRO_PATH}`}>
        <BackgroundCheckIntroPage
          isLoading={isLoading}
          onContinue={async () => {
            logEvent(APP_V2_APP_EVENTS.CLICKED_BACKGROUND_CHECK_BUTTON, {
              from: BACKGROUND_CHECK_PAGE_SOURCE.INTRO_PAGE,
              action: BACKGROUND_CHECK_BUTTON_ACTION.SUBMIT,
              workerId: worker.userId,
              shiftId,
              requiredData,
              returnTo,
            });

            try {
              await createBackgroundCheckCandidate({
                workerId: worker.userId,
              });
              goToNextStep(BACKGROUND_CHECK_INTRO_PATH);
            } catch (error) {
              logError(APP_V2_APP_EVENTS.FAILED_TO_CREATE_BACKGROUND_CHECK_CANDIDATE, {
                error,
                metadata: {
                  shiftId,
                  requiredData,
                  returnTo,
                },
              });
              showErrorToast("Something went wrong. Please try again later, or contact support!");
            }
          }}
        />
      </Route>
      <Route exact path={`${match.url}/${BACKGROUND_CHECK_DETAILS_PATH}`}>
        <BackgroundCheckDetailsPage
          onContinue={() => {
            logEvent(APP_V2_APP_EVENTS.CLICKED_BACKGROUND_CHECK_BUTTON, {
              from: BACKGROUND_CHECK_PAGE_SOURCE.DETAILS_PAGE,
              action: BACKGROUND_CHECK_BUTTON_ACTION.SUBMIT,
              workerId: worker.userId,
              shiftId,
              requiredData,
              returnTo,
            });
            goToNextStep(BACKGROUND_CHECK_DETAILS_PATH);
          }}
          onBack={() => {
            logEvent(APP_V2_APP_EVENTS.CLICKED_BACKGROUND_CHECK_BUTTON, {
              from: BACKGROUND_CHECK_PAGE_SOURCE.DETAILS_PAGE,
              action: BACKGROUND_CHECK_BUTTON_ACTION.BACK,
              workerId: worker.userId,
              shiftId,
              requiredData,
              returnTo,
            });
            goToPreviousStep(BACKGROUND_CHECK_DETAILS_PATH);
          }}
        />
      </Route>
      <Route exact path={`${match.url}/${BACKGROUND_CHECK_CONSENT_PATH}`}>
        <BackgroundCheckConsentPage
          returnToIntroPage={() => {
            history.replace(`${BACKGROUND_CHECK_INTRO_PATH}${location.search}`);
          }}
          onContinue={() => {
            logEvent(APP_V2_APP_EVENTS.CLICKED_BACKGROUND_CHECK_BUTTON, {
              from: BACKGROUND_CHECK_PAGE_SOURCE.CONSENT_PAGE,
              action: BACKGROUND_CHECK_BUTTON_ACTION.SUBMIT,
              workerId: worker.userId,
              shiftId,
              requiredData,
              returnTo,
            });
            goToNextStep(BACKGROUND_CHECK_CONSENT_PATH);
          }}
          onBack={() => {
            logEvent(APP_V2_APP_EVENTS.CLICKED_BACKGROUND_CHECK_BUTTON, {
              from: BACKGROUND_CHECK_PAGE_SOURCE.CONSENT_PAGE,
              action: BACKGROUND_CHECK_BUTTON_ACTION.BACK,
              workerId: worker.userId,
              shiftId,
              requiredData,
              returnTo,
            });
            goToPreviousStep(BACKGROUND_CHECK_CONSENT_PATH);
          }}
        />
      </Route>
      <Route exact path={`${match.url}/${BACKGROUND_CHECK_COMPLETED_PATH}`}>
        <BackgroundCheckCompletedPage hideHeader shiftId={shiftId} returnTo={returnTo} />
      </Route>
    </Switch>
  );
}
